import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type { CreateWalkerRegistrationRequest } from "../types/walkers";

type SubmitState = "idle" | "submitting" | "submitted" | "error";

type FormState = {
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  homePostcode: string;
  town: string;
  county: string;
  serviceRadiusMiles: string;
  bio: string;
  experienceSummary: string;
  dogSizeSmall: boolean;
  dogSizeMedium: boolean;
  dogSizeLarge: boolean;
  soloWalks: boolean;
  groupWalks: boolean;
  puppyVisits: boolean;
  elderlyDogCare: boolean;
  dbsChecked: boolean;
  insured: boolean;
};

const initialForm: FormState = {
  email: "",
  displayName: "",
  firstName: "",
  lastName: "",
  homePostcode: "",
  town: "",
  county: "",
  serviceRadiusMiles: "5",
  bio: "",
  experienceSummary: "",
  dogSizeSmall: true,
  dogSizeMedium: true,
  dogSizeLarge: true,
  soloWalks: false,
  groupWalks: true,
  puppyVisits: false,
  elderlyDogCare: false,
  dbsChecked: false,
  insured: false,
};

const MAX_PHOTOS = 4;

const JoinWalkerPage: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(initialForm);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const previews = useMemo(
    () =>
      selectedFiles.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [selectedFiles]
  );

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (submitState !== "idle") setSubmitState("idle");
    if (submitError) setSubmitError("");
    if (successMessage) setSuccessMessage("");
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const filtered = files
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, MAX_PHOTOS);

    setSelectedFiles(filtered);

    if (submitState !== "idle") setSubmitState("idle");
    if (submitError) setSubmitError("");
    if (successMessage) setSuccessMessage("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!form.email.trim()) {
      setSubmitState("error");
      setSubmitError("Please enter your email address.");
      return;
    }

    if (!form.firstName.trim()) {
      setSubmitState("error");
      setSubmitError("Please enter your first name.");
      return;
    }

    if (!form.homePostcode.trim()) {
      setSubmitState("error");
      setSubmitError("Please enter your home postcode.");
      return;
    }

    if (selectedFiles.length > MAX_PHOTOS) {
      setSubmitState("error");
      setSubmitError(`You can upload up to ${MAX_PHOTOS} photos.`);
      return;
    }

    try {
      setSubmitState("submitting");
      setSubmitError("");
      setSuccessMessage("");

      const request: CreateWalkerRegistrationRequest = {
        email: form.email.trim(),
        displayName: form.displayName.trim() || null,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || null,
        homePostcode: form.homePostcode.trim(),
        town: form.town.trim() || null,
        county: form.county.trim() || null,
        serviceRadiusMiles: Number(form.serviceRadiusMiles) || 5,
        bio: form.bio.trim() || null,
        experienceSummary: form.experienceSummary.trim() || null,
        dogSizeSmall: form.dogSizeSmall,
        dogSizeMedium: form.dogSizeMedium,
        dogSizeLarge: form.dogSizeLarge,
        soloWalks: form.soloWalks,
        groupWalks: form.groupWalks,
        puppyVisits: form.puppyVisits,
        elderlyDogCare: form.elderlyDogCare,
        dbsChecked: form.dbsChecked,
        insured: form.insured,
      };

      const result = await walkiesClient.registerWalker(request);

      if (selectedFiles.length > 0) {
        await walkiesClient.uploadWalkerPhotos(result.walkerProfileId, selectedFiles);
      }

      setSubmitState("submitted");
      setSuccessMessage(
        selectedFiles.length > 0
          ? "Your walker profile is now live and your photos have been uploaded."
          : "Your walker profile is now live."
      );

      navigate(`/walkers/${result.slug}`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to register walker.";
      setSubmitState("error");
      setSubmitError(message);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.backLink}>
            ← Back to home
          </Link>
        </div>

        <section style={styles.heroCard}>
          <div style={styles.badge}>Join as a walker</div>
          <h1 style={styles.title}>Create your walker profile</h1>
          <p style={styles.lead}>
            Register now and make your profile visible to local dog owners.
          </p>
        </section>

        {submitState === "error" && submitError && (
          <div style={styles.errorBox}>{submitError}</div>
        )}

        {submitState === "submitted" && successMessage && (
          <div style={styles.successBox}>{successMessage}</div>
        )}

        <section style={styles.formCard}>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.grid}>
              <div style={styles.field}>
                <label htmlFor="firstName" style={styles.label}>
                  First name *
                </label>
                <input
                  id="firstName"
                  value={form.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="lastName" style={styles.label}>
                  Last name
                </label>
                <input
                  id="lastName"
                  value={form.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="email" style={styles.label}>
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="displayName" style={styles.label}>
                  Display name
                </label>
                <input
                  id="displayName"
                  value={form.displayName}
                  onChange={(e) => update("displayName", e.target.value)}
                  style={styles.input}
                  placeholder="Optional public display name"
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="homePostcode" style={styles.label}>
                  Home postcode *
                </label>
                <input
                  id="homePostcode"
                  value={form.homePostcode}
                  onChange={(e) => update("homePostcode", e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="serviceRadiusMiles" style={styles.label}>
                  Service radius (miles)
                </label>
                <input
                  id="serviceRadiusMiles"
                  type="number"
                  min="1"
                  max="50"
                  step="0.5"
                  value={form.serviceRadiusMiles}
                  onChange={(e) => update("serviceRadiusMiles", e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="town" style={styles.label}>
                  Town
                </label>
                <input
                  id="town"
                  value={form.town}
                  onChange={(e) => update("town", e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="county" style={styles.label}>
                  County
                </label>
                <input
                  id="county"
                  value={form.county}
                  onChange={(e) => update("county", e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldFull}>
                <label htmlFor="experienceSummary" style={styles.label}>
                  Experience summary
                </label>
                <input
                  id="experienceSummary"
                  value={form.experienceSummary}
                  onChange={(e) => update("experienceSummary", e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 5 years walking local dogs"
                />
              </div>

              <div style={styles.fieldFull}>
                <label htmlFor="bio" style={styles.label}>
                  Bio
                </label>
                <textarea
                  id="bio"
                  rows={6}
                  value={form.bio}
                  onChange={(e) => update("bio", e.target.value)}
                  style={styles.textarea}
                  placeholder="Tell owners about your service, experience and approach."
                />
              </div>

              <div style={styles.fieldFull}>
                <label htmlFor="photos" style={styles.label}>
                  Profile photos
                </label>
                <input
                  id="photos"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={styles.input}
                />
                <div style={styles.helpText}>
                  Add up to {MAX_PHOTOS} photos. The first photo will be used as the main profile photo.
                </div>

                {previews.length > 0 && (
                  <div style={styles.previewGrid}>
                    {previews.map((preview) => (
                      <div key={preview.url} style={styles.previewCard}>
                        <img
                          src={preview.url}
                          alt={preview.name}
                          style={styles.previewImage}
                        />
                        <div style={styles.previewName}>{preview.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Dog sizes</h2>
              <div style={styles.checkGrid}>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.dogSizeSmall}
                    onChange={(e) => update("dogSizeSmall", e.target.checked)}
                  />
                  <span>Small dogs</span>
                </label>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.dogSizeMedium}
                    onChange={(e) => update("dogSizeMedium", e.target.checked)}
                  />
                  <span>Medium dogs</span>
                </label>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.dogSizeLarge}
                    onChange={(e) => update("dogSizeLarge", e.target.checked)}
                  />
                  <span>Large dogs</span>
                </label>
              </div>
            </div>

            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Services</h2>
              <div style={styles.checkGrid}>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.soloWalks}
                    onChange={(e) => update("soloWalks", e.target.checked)}
                  />
                  <span>Solo walks</span>
                </label>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.groupWalks}
                    onChange={(e) => update("groupWalks", e.target.checked)}
                  />
                  <span>Group walks</span>
                </label>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.puppyVisits}
                    onChange={(e) => update("puppyVisits", e.target.checked)}
                  />
                  <span>Puppy visits</span>
                </label>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.elderlyDogCare}
                    onChange={(e) => update("elderlyDogCare", e.target.checked)}
                  />
                  <span>Elderly dog care</span>
                </label>
              </div>
            </div>

            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Trust signals</h2>
              <div style={styles.checkGrid}>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.dbsChecked}
                    onChange={(e) => update("dbsChecked", e.target.checked)}
                  />
                  <span>DBS checked</span>
                </label>
                <label style={styles.checkItem}>
                  <input
                    type="checkbox"
                    checked={form.insured}
                    onChange={(e) => update("insured", e.target.checked)}
                  />
                  <span>Insured</span>
                </label>
              </div>
            </div>

            <div style={styles.actions}>
              <button
                type="submit"
                style={styles.submitButton}
                disabled={submitState === "submitting"}
              >
                {submitState === "submitting"
                  ? "Creating profile..."
                  : "Create walker profile"}
              </button>

              <Link to="/" style={styles.secondaryLink}>
                Cancel
              </Link>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "32px 16px 56px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 980,
    margin: "0 auto",
  },
  topRow: {
    marginBottom: 18,
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    color: "rgba(255,255,255,0.9)",
    textDecoration: "none",
    fontWeight: 700,
  },
  heroCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    marginBottom: 22,
  },
  badge: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.12)",
    marginBottom: 14,
  },
  title: {
    margin: "0 0 10px",
    fontSize: 42,
    lineHeight: 1.02,
    letterSpacing: "-0.04em",
    fontWeight: 900,
  },
  lead: {
    margin: 0,
    color: "rgba(255,255,255,0.82)",
    lineHeight: 1.7,
  },
  formCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  errorBox: {
    background: "rgba(155, 28, 28, 0.18)",
    color: "#ffffff",
    border: "1px solid rgba(255, 219, 219, 0.34)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
  },
  successBox: {
    background: "rgba(212, 255, 217, 0.18)",
    color: "#ffffff",
    border: "1px solid rgba(212, 255, 217, 0.38)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
  },
  form: {
    display: "grid",
    gap: 22,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  field: {
    display: "grid",
    gap: 8,
  },
  fieldFull: {
    display: "grid",
    gap: 8,
    gridColumn: "1 / -1",
  },
  label: {
    fontWeight: 800,
    fontSize: 14,
    color: "#213a33",
  },
  input: {
    width: "100%",
    font: "inherit",
    padding: "14px 14px",
    borderRadius: 14,
    border: "1px solid rgba(24,32,29,0.16)",
    background: "#ffffff",
    color: "#18201d",
    outline: "none",
  },
  textarea: {
    width: "100%",
    font: "inherit",
    padding: "14px 14px",
    borderRadius: 14,
    border: "1px solid rgba(24,32,29,0.16)",
    background: "#ffffff",
    color: "#18201d",
    outline: "none",
    resize: "vertical",
  },
  helpText: {
    fontSize: 13,
    color: "rgba(24,32,29,0.65)",
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    marginTop: 10,
  },
  previewCard: {
    background: "rgba(24,32,29,0.05)",
    borderRadius: 14,
    padding: 10,
  },
  previewImage: {
    width: "100%",
    height: 120,
    objectFit: "cover",
    borderRadius: 12,
    display: "block",
    background: "#ebe2d4",
  },
  previewName: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 700,
    color: "#213a33",
    wordBreak: "break-word",
  },
  section: {
    display: "grid",
    gap: 12,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 22,
    color: "#213a33",
  },
  checkGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 14,
    background: "rgba(24,32,29,0.05)",
    color: "#18201d",
    fontWeight: 700,
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  submitButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    background: "#213a33",
    color: "#ffffff",
  },
  secondaryLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#ebe2d4",
    color: "#213a33",
    textDecoration: "none",
  },
};

export default JoinWalkerPage;